import DeleteIcon from '@mui/icons-material/Delete';
import DirectionsBusIcon from '@mui/icons-material/DirectionsBus';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import TwoWheelerIcon from '@mui/icons-material/TwoWheeler';
import { Grid, Typography, Box } from '@mui/material';
import { find, map } from 'lodash';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Button, ContentBox, Modal } from '../../components';
import { IRREGULARITIES, VEHICLES } from '../../routes';
import {
  fetchDeleteVehicle,
  fetchGetVehicleList,
  fetchSetDefaultVehicle,
  fetchGetIrregularitiesByPlate,
  useDispatch,
  useSelector,
  vehiclesSlice,
} from '../../stores';
import { palette } from '../../theme';
import { handleApiReturn } from '../../utils';
import { CreateVehicleModal } from './modals/CreateVehicleModal';
import {
  ButtonsGrid,
  DeleteButton,
  StyledModalWrapper,
  VehicleButton,
} from './styles';

export const Vehicles: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { driverId } = useSelector((state) => state.profileReducer);
  const { vehicles, vehiclesLoading, deleteVehicleIsOpen } = useSelector(
    (state) => state.vehiclesReducer,
  );
  const { deactivateDiscount } = useSelector(
    (state) => state.ticketPurchaseReducer,
  );
  const [vehicleOnFocus, setVehicleOnFocus] = useState<{
    id: number;
    plate: string;
    type: string;
    model: string;
    default: number;
    used: number;
    irregularitiesCount?: number;
  } | null>(null);

  const selectType = (type: string) => {
    switch (type) {
      case 'Carro':
        return <DirectionsCarIcon />;
      case 'Moto':
        return <TwoWheelerIcon />;
      case 'Ônibus':
        return <DirectionsBusIcon />;
      default:
        return <LocalShippingIcon />;
    }
  };

  const handleGoToIrregularities = () => {
    navigate(IRREGULARITIES);
  };

  const handleStandardizeVehicle = (vehicleId: number) => {
    dispatch(
      fetchSetDefaultVehicle({
        driverId: localStorage.getItem('NITROT:driverId') || '',
        vehicleId: vehicleId.toString(),
      }),
    ).then((res: any) => {
      handleApiReturn(
        res,
        () => {
          toast.success('Veículo padronizado com sucesso.');
          dispatch(
            fetchGetVehicleList({
              driverId: localStorage.getItem('NITROT:driverId') || '',
            }),
          );
        },
        null,
      );
    });
  };

  const handleDeleteVehicle = () => {
    if (vehicleOnFocus) {
      // Check if item to be deleted is default, and change default to another if yes
      if (vehicleOnFocus.default && vehicles.length > 1) {
        const item = find(vehicles, (x) => {
          return x.id !== vehicleOnFocus.id && x.default === 0;
        });

        if (item) {
          dispatch(
            fetchSetDefaultVehicle({
              driverId: localStorage.getItem('NITROT:driverId') || '',
              vehicleId: item.id.toString(),
            }),
          );
        }
      }

      dispatch(
        fetchDeleteVehicle({
          driverId: localStorage.getItem('NITROT:driverId') || '',
          vehicleId: vehicleOnFocus.id.toString(),
        }),
      ).then((res: any) => {
        handleApiReturn(
          res,
          () => {
            dispatch(
              fetchGetVehicleList({
                driverId: localStorage.getItem('NITROT:driverId') || '',
              }),
            );
            dispatch(vehiclesSlice.actions.toggleDeleteVehicleIsOpen());
            setVehicleOnFocus(null);
            toast.success('Veículo excluído com sucesso');
          },
          null,
        );
      });
    }
  };

  useEffect(() => {
    if (location.pathname === VEHICLES) {
      dispatch(
        fetchGetVehicleList({
          driverId: driverId.toString(),
        }),
      );
    }
  }, [location.pathname]);

  return (
    <ContentBox loading={vehiclesLoading}>
      {vehicles.length > 0 ? (
        <Box
          sx={{
            width: '100%',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'flex-start',
            alignItems: 'center',
            overflowY: 'auto',
            paddingTop: {
              xs: '20px',
              sm: 0,
            },
            paddingBottom: {
              xs: '20px',
              sm: 0,
            },
          }}
        >
          <Grid
            container
            rowSpacing={3}
            columnSpacing={{ xs: 1, sm: 2, md: 3 }}
            sx={{
              width: '100%',
              display: 'flex',
              justifyContent: 'flex-start',
              alignItems: 'center',
              marginLeft: 0,
              paddingBottom: {
                xs: 0,
                md: '50px',
              },
            }}
          >
            {map(vehicles, (item) => {
              const irregularitiesCount = item.irregularitiesCount ?? 0;
              const hasIrregularities = irregularitiesCount > 0;

              return (
                <ButtonsGrid item xs={12} sm={12} md={6} lg={6} xl={4}>
                  <VehicleButton
                    variant='outlined'
                    color={
                      !deactivateDiscount && item.used === 0
                        ? 'success'
                        : 'primary'
                    }
                    onClick={() => handleStandardizeVehicle(item.id)}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'flex-start',
                        width: '100%',
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          width: '100%',
                          backgroundColor: palette.grey[100],
                          padding: '10px 20px',
                          gap: 0.5,
                        }}
                      >
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            width: '100%',
                            gap: 1,
                            color: palette.primary.main,
                          }}
                        >
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1,
                              minWidth: 0,
                            }}
                          >
                            {selectType(item.type)}
                            <Typography
                              sx={{
                                fontSize: {
                                  xs: '14px',
                                  sm: '16px',
                                },
                                fontWeight: 600,
                                color: palette.primary.main,
                              }}
                              noWrap
                            >
                              {item.type} · {item.plate} · {item.model}
                            </Typography>
                          </Box>
                          {item.default === 1 ? (
                            <Box
                              sx={{
                                borderRadius: '999px',
                                padding: '2px 10px',
                                border: `1px solid ${palette.primary.main}`,
                                color: palette.primary.main,
                                fontSize: '11px',
                                fontWeight: 600,
                                whiteSpace: 'nowrap',
                              }}
                            >
                              Padrão
                            </Box>
                          ) : (
                            <DeleteButton
                              aria-label='delete'
                              size='small'
                              onClick={(event) => {
                                event.stopPropagation();
                                setVehicleOnFocus(item);
                                dispatch(
                                  vehiclesSlice.actions.toggleDeleteVehicleIsOpen(),
                                );
                              }}
                            >
                              <DeleteIcon
                                // fontSize='inherit'
                                fontSize='medium'
                                sx={{ color: palette.error.main }}
                              />
                            </DeleteButton>
                          )}
                        </Box>
                        {!deactivateDiscount && item.used === 0 && (
                          <Typography
                            variant='caption'
                            mt={0.5}
                            color={palette.success.main}
                            sx={{
                              fontSize: {
                                xs: '10px',
                                sm: '12px',
                              },
                            }}
                            noWrap
                          >
                            (COM DESCONTO)
                          </Typography>
                        )}
                      </Box>
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          marginTop: '8px',
                          width: '100%',
                          padding: '0px 20px',
                        }}
                      >
                        <Box
                          sx={{
                            borderRadius: '8px',
                            padding: '4px 10px',
                            backgroundColor:
                              irregularitiesCount === 0
                                ? palette.success.light
                                : palette.error.light,
                            color: 'white',
                            fontSize: '14px',
                            fontWeight: 500,
                            maxWidth: '50%',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}
                        >
                          {irregularitiesCount === 0
                            ? 'Regular'
                            : `${irregularitiesCount} irregularidades`}
                        </Box>
                        <Button
                          variant='contained'
                          color='secondary'
                          label='Regularizar'
                          size='small'
                          onClick={(event) => {
                            if (!hasIrregularities) return;
                            event.stopPropagation();
                            dispatch(
                              fetchGetIrregularitiesByPlate({
                                driverId: driverId.toString(),
                                plate: item.plate,
                              }),
                            );
                            handleGoToIrregularities();
                          }}
                          sx={{
                            paddingX: 1.5,
                            paddingY: 0.25,
                            minWidth: 0,
                            fontSize: 11,
                            borderRadius: '999px',
                            textTransform: 'none',
                            fontWeight: 500,
                            boxShadow: 2,
                            visibility: hasIrregularities
                              ? 'visible'
                              : 'hidden',
                            pointerEvents: hasIrregularities ? 'auto' : 'none',
                          }}
                        />
                      </Box>
                    </Box>
                  </VehicleButton>
                </ButtonsGrid>
              );
            })}
          </Grid>
        </Box>
      ) : (
        <Grid
          sx={{
            height: '100%',
            width: '100%',
            display: 'flex',
            flexGrow: 1,
            margin: 'auto',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <Typography
            color={palette.text.main}
            sx={{
              fontSize: {
                xs: '12px',
                sm: '14px',
                lg: '18px',
              },
            }}
          >
            Nenhum veículo cadastrado
          </Typography>
        </Grid>
      )}
      <Grid
        sx={{
          width: '100%',
          display: 'flex',
          flexGrow: 1,
          margin: 'auto',
          justifyContent: 'center',
          alignItems: 'flex-end',
        }}
      >
        <Button
          variant='contained'
          label='Cadastrar veículo'
          type='submit'
          onClick={() =>
            dispatch(vehiclesSlice.actions.toggleCreateVehicleIsOpen())
          }
          color='secondary'
          loading={vehiclesLoading}
          style={{ width: '100%', marginTop: 10 }}
        />
      </Grid>
      <Modal
        open={deleteVehicleIsOpen}
        onClose={() => {
          dispatch(vehiclesSlice.actions.toggleDeleteVehicleIsOpen());
        }}
        title='Deseja deletar o veículo?'
      >
        <StyledModalWrapper>
          <Typography
            color='primary'
            m={1}
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            {vehicleOnFocus && selectType(vehicleOnFocus.type)}{' '}
            {vehicleOnFocus?.plate}
          </Typography>
          <Button
            variant='contained'
            label='Deletar'
            type='submit'
            onClick={handleDeleteVehicle}
            color='secondary'
            loading={vehiclesLoading}
            style={{ width: '100%' }}
          />
        </StyledModalWrapper>
      </Modal>
      <CreateVehicleModal />
    </ContentBox>
  );
};
